import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, UploadCloud, Trash2, Search, Save, Loader2, Database, FileText } from "lucide-react";

export default function PonderrAgentRAG() {
  const [tab, setTab] = useState("ingest");
  const [tax, setTax] = useState({ countries: [], boards: [], grades: [], subjects: [] });
  const [country, setCountry] = useState("");
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState("");
  const [docType, setDocType] = useState("textbook");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [vectorCount, setVectorCount] = useState(0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    api.get("/ponderr-agent/taxonomy").then(r => setTax(r.data)).catch(() => {});
    loadDocs();
    api.get("/ponderr-agent/config").then(r => setPrompt(r.data.system_prompt)).catch(() => {});
  }, []);

  useEffect(() => {
    if (board && grade && subjectId)
      api.get("/ponderr-agent/taxonomy/chapters", { params: { board_code: board, grade, subject_id: subjectId } })
        .then(r => setChapters(r.data)).catch(() => setChapters([]));
    else setChapters([]);
  }, [board, grade, subjectId]);

  const loadDocs = () => api.get("/ponderr-agent/documents").then(r => {
    setDocs(r.data.documents); setVectorCount(r.data.total_vector_chunks);
  }).catch(() => {});

  const ingest = async () => {
    if (!file || !board || !grade) { toast.info("Pick a file, board and grade"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("board_code", board); fd.append("grade", grade);
    fd.append("subject_id", subjectId); fd.append("chapter_id", chapterId);
    fd.append("country", country); fd.append("doc_type", docType);
    try {
      const r = await api.post("/ponderr-agent/ingest", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.vector_status === "embedded") toast.success(`Ingested ${r.data.chunk_count} chunks with vectors`);
      else toast.warning(`Stored ${r.data.chunk_count} chunks (${r.data.vector_status}). ${r.data.embed_error || ""}`);
      setFile(null); loadDocs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ingestion failed"); }
    finally { setBusy(false); }
  };

  const runQuery = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/ponderr-agent/query", { query: q, board_code: board || undefined, grade: grade || undefined });
      setResults(r.data);
    } catch { toast.error("Query failed"); } finally { setBusy(false); }
  };

  const saveConfig = async () => {
    try { await api.put("/ponderr-agent/config", { system_prompt: prompt }); toast.success("Socratic config saved"); }
    catch { toast.error("Save failed"); }
  };

  const del = async (doc_id) => { await api.delete(`/ponderr-agent/documents/${doc_id}`); loadDocs(); };

  return (
    <div className="space-y-4" data-testid="ponderr-agent-rag">
      <div className="flex items-center gap-2">
        <Bot size={20} />
        <h2 className="display text-xl font-bold">Ponderr AI Agent Engine</h2>
        <span className="nb-badge bg-[#86EFAC] text-xs ml-1"><Database size={11} /> {vectorCount} vector chunks</span>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["ingest", "Ingest Curriculum"], ["docs", "Knowledge Base"], ["query", "Test Retrieval"], ["config", "Socratic Tuning"]].map(([id, l]) => (
          <button key={id} className={`nb-btn text-xs ${tab === id ? "" : "nb-btn-ghost"}`} onClick={() => setTab(id)} data-testid={`rag-tab-${id}`}>{l}</button>
        ))}
      </div>

      {/* Shared taxonomy selectors */}
      {(tab === "ingest" || tab === "query") && (
        <div className="nb-card p-4 bg-white border-2 border-black grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <select className="nb-select text-sm" value={country} onChange={e => setCountry(e.target.value)} data-testid="rag-country">
            <option value="">Country…</option>{tax.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="nb-select text-sm" value={board} onChange={e => setBoard(e.target.value)} data-testid="rag-board">
            <option value="">Board…</option>{tax.boards.map(b => <option key={b.code} value={b.code}>{b.code}</option>)}
          </select>
          <select className="nb-select text-sm" value={grade} onChange={e => setGrade(e.target.value)} data-testid="rag-grade">
            <option value="">Grade…</option>{tax.grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select className="nb-select text-sm" value={subjectId} onChange={e => setSubjectId(e.target.value)} data-testid="rag-subject">
            <option value="">Subject…</option>{tax.subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
          </select>
          <select className="nb-select text-sm" value={chapterId} onChange={e => setChapterId(e.target.value)} disabled={!chapters.length} data-testid="rag-chapter">
            <option value="">Chapter…</option>{chapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.title}</option>)}
          </select>
        </div>
      )}

      {tab === "ingest" && (
        <div className="nb-card p-5 bg-[#DDD6FE] border-2 border-black space-y-3" data-testid="rag-ingest-panel">
          <div className="grid sm:grid-cols-2 gap-3">
            <select className="nb-select text-sm" value={docType} onChange={e => setDocType(e.target.value)} data-testid="rag-doctype">
              <option value="textbook">PDF Textbook</option>
              <option value="revision_notes">Revision Notes</option>
              <option value="exemplar">Exemplar Question Set</option>
            </select>
            <input type="file" accept=".pdf,.txt,.md" onChange={e => setFile(e.target.files[0])} className="text-sm" data-testid="rag-file" />
          </div>
          <button className="nb-btn" disabled={busy} onClick={ingest} data-testid="rag-ingest-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} Parse, Chunk &amp; Embed
          </button>
          <p className="text-xs opacity-70">Uses the platform Gemini key with <b>gemini-embedding-001</b> (768-dim). Vectors are stored natively in MongoDB. Without a platform key, chunks are stored for keyword retrieval.</p>
        </div>
      )}

      {tab === "docs" && (
        <div className="nb-card border-2 border-black overflow-x-auto" data-testid="rag-docs-panel">
          <table className="nb-table w-full text-sm">
            <thead><tr><th>File</th><th>Board/Grade</th><th>Type</th><th>Chunks</th><th>Vectors</th><th></th></tr></thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.doc_id} data-testid={`rag-doc-${d.doc_id}`}>
                  <td className="flex items-center gap-1"><FileText size={13} /> {d.filename}</td>
                  <td>{d.board_code} · {d.grade}</td>
                  <td>{d.doc_type}</td>
                  <td>{d.chunk_count}</td>
                  <td><span className={`nb-badge text-xs ${d.vector_status === "embedded" ? "bg-[#86EFAC]" : d.vector_status === "failed" ? "bg-[#FCA5A5]" : "bg-[#FDE047]"}`}>{d.vector_status}</span></td>
                  <td><button onClick={() => del(d.doc_id)} data-testid={`rag-del-${d.doc_id}`}><Trash2 size={14} className="text-red-600" /></button></td>
                </tr>
              ))}
              {!docs.length && <tr><td colSpan={6} className="text-center py-6 opacity-60">No documents ingested yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "query" && (
        <div className="nb-card p-5 bg-white border-2 border-black space-y-3" data-testid="rag-query-panel">
          <div className="flex gap-2">
            <input className="nb-input flex-1 text-sm" placeholder="Semantic query…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && runQuery()} data-testid="rag-query-input" />
            <button className="nb-btn" disabled={busy} onClick={runQuery} data-testid="rag-query-btn"><Search size={15} /> Retrieve</button>
          </div>
          {results && (
            <div className="space-y-2" data-testid="rag-query-results">
              <div className="text-xs opacity-70">Mode: <b>{results.mode}</b> {results.note && `· ${results.note}`}</div>
              {results.results.map((r, i) => (
                <div key={i} className="nb-card-sm p-3 bg-[#FAF7F0] border border-black text-sm">
                  <div className="text-[10px] font-bold opacity-60">score {r.score} · {r.chapter_id || "—"}</div>
                  {(r.text || "").slice(0, 260)}…
                </div>
              ))}
              {!results.results.length && <div className="text-sm opacity-60">No matches.</div>}
            </div>
          )}
        </div>
      )}

      {tab === "config" && (
        <div className="nb-card p-5 bg-white border-2 border-black space-y-3" data-testid="rag-config-panel">
          <div className="text-sm font-bold">Socratic System Prompt</div>
          <textarea className="nb-input w-full text-sm min-h-[180px]" value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="rag-config-prompt" />
          <button className="nb-btn" onClick={saveConfig} data-testid="rag-config-save"><Save size={15} /> Save Personality</button>
        </div>
      )}
    </div>
  );
}
